import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBranchContext } from "@/contexts/BranchContext";
import { useFeatureEntitlements } from "@/hooks/useFeatureEntitlements";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  computeProfitPerSku,
  computeDeadStockLiquidation,
  computeReorderSuggestions,
  type InvRow,
  type SaleLine,
} from "@/lib/businessIntelligence";
import { businessHasLogisticsData } from "@/lib/reorderLeadTime";
import { subDays } from "date-fns";
import { AlertTriangle, TrendingUp, Package, ArrowLeft, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";

const DEAD_STOCK_STALE_DAYS = 45;
const LIQUIDATION_DISCOUNT_PCT = 15;
const VELOCITY_WINDOW_DAYS = 7;
const PROFIT_WINDOW_DAYS = 30;

function formatK(n: number): string {
  return `K${Math.round(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

export default function BusinessIntelligencePage() {
  const { businessId } = useAuth();
  const { selectedBranchId } = useBranchContext();
  const { businessIntelligence } = useFeatureEntitlements();
  const { openPaywall } = usePaywallModal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);
  const [computed, setComputed] = useState<{
    profitRows: ReturnType<typeof computeProfitPerSku>;
    deadRows: ReturnType<typeof computeDeadStockLiquidation>;
    reorderRows: ReturnType<typeof computeReorderSuggestions>;
    hasLogisticsData: boolean;
  }>({ profitRows: [], deadRows: [], reorderRows: [], hasLogisticsData: false });

  useEffect(() => {
    if (businessIntelligence === "none") {
      openPaywall("business_intelligence");
    }
  }, [businessIntelligence, openPaywall]);

  const fetchData = useCallback(async () => {
    if (!businessId || businessIntelligence === "none") {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const branchFilter = selectedBranchId !== "all" ? selectedBranchId : null;
    const sinceProfit = subDays(new Date(), PROFIT_WINDOW_DAYS).toISOString();
    const sinceVelocity = subDays(new Date(), VELOCITY_WINDOW_DAYS).toISOString();
    const sinceWide = subDays(new Date(), 400).toISOString();

    try {
      let salesQ = supabase
        .from("sales")
        .select("item_id, quantity, final_amount, total_price, unit_price, created_at, voided_at, branch_id")
        .eq("business_id", businessId)
        .gte("created_at", sinceWide)
        .order("created_at", { ascending: false });
      if (branchFilter) salesQ = salesQ.eq("branch_id", branchFilter);

      let invQ = supabase
        .from("inventory")
        .select("id, sku, name, current_stock, buying_price, selling_price, branch_id")
        .eq("business_id", businessId);
      if (branchFilter) invQ = invQ.eq("branch_id", branchFilter);

      const poQ = supabase
        .from("purchase_orders")
        .select("id, supplier_id, order_date")
        .eq("business_id", businessId)
        .order("order_date", { ascending: false })
        .limit(200);

      const supQ = supabase.from("suppliers").select("id, default_lead_time_days").eq("business_id", businessId);

      const [salesRes, invRes, poRes, supRes] = await Promise.all([salesQ, invQ, poQ, supQ]);

      if (salesRes.error) throw salesRes.error;
      if (invRes.error) throw invRes.error;

      const salesData = (salesRes.data ?? []) as SaleLine[];

      const pos = (poRes.data ?? []) as { id: string; supplier_id: string; order_date: string }[];
      const poIds = pos.map((p) => p.id);
      let deliveries: {
        purchase_order_id: string;
        delivery_date: string;
        received_at: string | null;
        status: string;
      }[] = [];
      if (poIds.length > 0) {
        const delRes = await supabase
          .from("deliveries")
          .select("purchase_order_id, delivery_date, received_at, status")
          .in("purchase_order_id", poIds);
        if (delRes.error) throw delRes.error;
        deliveries = delRes.data ?? [];
      }

      let pols: { purchase_order_id: string; inventory_id: string | null }[] = [];
      if (poIds.length > 0) {
        const polRes = await supabase
          .from("purchase_order_lines")
          .select("purchase_order_id, inventory_id")
          .in("purchase_order_id", poIds);
        if (polRes.error) throw polRes.error;
        pols = polRes.data ?? [];
      }

      const suppliers = (supRes.data ?? []) as { id: string; default_lead_time_days: number | null }[];

      const salesForProfit = salesData.filter((s) => new Date(s.created_at) >= new Date(sinceProfit));
      const salesForVelocity = salesData.filter((s) => new Date(s.created_at) >= new Date(sinceVelocity));

      const profitRows = computeProfitPerSku(salesForProfit, (invRes.data ?? []) as InvRow[], sinceProfit);
      const deadRows = computeDeadStockLiquidation((invRes.data ?? []) as InvRow[], salesData, {
        staleDays: DEAD_STOCK_STALE_DAYS,
        discountPct: LIQUIDATION_DISCOUNT_PCT,
      });
      const reorderRows = computeReorderSuggestions(
        (invRes.data ?? []) as InvRow[],
        salesForVelocity,
        pos,
        deliveries,
        pols,
        suppliers,
        { velocityDays: VELOCITY_WINDOW_DAYS, minUnitsPerWeek: 0.5 }
      );

      setComputed({
        profitRows,
        deadRows,
        reorderRows,
        hasLogisticsData: businessHasLogisticsData(suppliers, deliveries),
      });
      setLastUpdatedAt(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load intelligence");
    } finally {
      setLoading(false);
    }
  }, [businessId, businessIntelligence, selectedBranchId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!businessId || businessIntelligence === "none") return;

    const scheduleRefresh = () => {
      if (refreshTimeoutRef.current != null) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
      // Coalesce bursts of DB events into one fetch.
      refreshTimeoutRef.current = window.setTimeout(() => {
        void fetchData();
      }, 600);
    };

    const channel = supabase
      .channel(`bi-live-${businessId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "inventory", filter: `business_id=eq.${businessId}` },
        scheduleRefresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "sales", filter: `business_id=eq.${businessId}` },
        scheduleRefresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "purchase_orders", filter: `business_id=eq.${businessId}` },
        scheduleRefresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "suppliers", filter: `business_id=eq.${businessId}` },
        scheduleRefresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "purchase_order_lines" },
        scheduleRefresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "deliveries" },
        scheduleRefresh
      )
      .subscribe();

    const pollId = window.setInterval(() => {
      void fetchData();
    }, 30000);

    const onFocus = () => {
      void fetchData();
    };
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(pollId);
      if (refreshTimeoutRef.current != null) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      void supabase.removeChannel(channel);
    };
  }, [businessId, businessIntelligence, fetchData]);

  const { profitRows, deadRows, reorderRows, hasLogisticsData } = computed;

  const topReorder = useMemo(
    () => reorderRows.filter((r) => r.has_reorder_advice && r.is_urgent).slice(0, 8),
    [reorderRows]
  );
  const velocityOnlyRows = useMemo(
    () => reorderRows.filter((r) => !r.has_reorder_advice),
    [reorderRows]
  );

  if (businessIntelligence === "none") {
    return (
      <div className="space-y-4 p-4 max-w-3xl mx-auto">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/" className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>Stock intelligence</CardTitle>
            <CardDescription>Upgrade to Pro to unlock profit per product, reorder timing, and dead stock alerts.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
            <Link to="/" className="gap-2 text-muted-foreground">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
          </Button>
          <h1 className="text-2xl font-display font-bold">Stock intelligence</h1>
          <p className="text-sm text-muted-foreground">
            Inventory-centric metrics: margin by SKU, reorder timing, and liquidation scenarios — branch filter follows the header switcher.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdatedAt && (
            <span className="text-xs text-muted-foreground">
              Updated {lastUpdatedAt.toLocaleTimeString()}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => void fetchData()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="h-5 w-5 text-primary" />
                Profit per product (last {PROFIT_WINDOW_DAYS} days)
              </CardTitle>
              <CardDescription className="space-y-2">
                <p className="text-muted-foreground">Sorted by gross profit (highest first).</p>
                <p className="text-xs text-muted-foreground">
                  Restocking (inventory quantity changes) and expense entries do not change units sold/revenue here.
                  This table is sales-based; COGS uses the current buying price saved on the inventory item.
                </p>
                <details className="rounded-md border border-border/60 bg-muted/25 text-sm [&_summary::-webkit-details-marker]:hidden">
                  <summary className="cursor-pointer list-none px-3 py-2 font-medium text-primary hover:underline">
                    How margin is calculated
                  </summary>
                  <div className="space-y-2 border-t border-border/50 px-3 pb-3 pt-2 text-muted-foreground">
                    <p>
                      For each sale in the last {PROFIT_WINDOW_DAYS} days that links to a product, we add up{" "}
                      <strong className="text-foreground">revenue</strong> from the sale and estimate{" "}
                      <strong className="text-foreground">cost of goods (COGS)</strong> as{" "}
                      <code className="rounded bg-muted px-1 text-xs text-foreground">units sold × current buying price</code>{" "}
                      from that product row in inventory.
                    </p>
                    <p>
                      <strong className="text-foreground">Gross profit</strong> = revenue minus COGS.{" "}
                      <strong className="text-foreground">Margin %</strong> = (gross profit ÷ revenue) × 100 when revenue is positive.
                      Ranking uses gross profit. This is gross margin only — rent, wages, and other shop costs are not split per product.
                    </p>
                    <p className="text-xs">
                      Update buying prices after restocks so cost matches what you expect; we use today&apos;s price on the product, not each past purchase.
                    </p>
                  </div>
                </details>
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {profitRows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No sales with linked products in this window.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-2">SKU</th>
                      <th className="py-2 pr-2">Product</th>
                      <th className="py-2 pr-2 text-right">Stock now</th>
                      <th className="py-2 pr-2 text-right">Buying now</th>
                      <th className="py-2 pr-2 text-right">Revenue</th>
                      <th className="py-2 pr-2 text-right">COGS est.</th>
                      <th className="py-2 pr-2 text-right">Gross profit</th>
                      <th className="py-2 text-right">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitRows.slice(0, 40).map((r) => (
                      <tr key={r.item_id} className="border-b border-border/60">
                        <td className="py-2 pr-2 font-mono text-xs">{r.sku}</td>
                        <td className="py-2 pr-2 max-w-[180px] truncate">{r.name}</td>
                        <td className="py-2 pr-2 text-right">{r.current_stock}</td>
                        <td className="py-2 pr-2 text-right">{formatK(r.buying_price_used)}</td>
                        <td className="py-2 pr-2 text-right">{formatK(r.revenue)}</td>
                        <td className="py-2 pr-2 text-right">{formatK(r.cogs_estimate)}</td>
                        <td className="py-2 pr-2 text-right font-medium">{formatK(r.gross_profit)}</td>
                        <td className="py-2 text-right text-muted-foreground">
                          {r.margin_pct != null ? `${r.margin_pct.toFixed(0)}%` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="h-5 w-5 text-primary" />
                Reorder intelligence
              </CardTitle>
              <CardDescription>
                Uses sales pace from the last {VELOCITY_WINDOW_DAYS} days. Reorder timing only when delivery days come from{" "}
                <Link to="/suppliers" className="underline underline-offset-2">Suppliers</Link> (on a PO) or{" "}
                <Link to="/transport-logistics" className="underline underline-offset-2">Transport &amp; Logistics</Link> delivery history.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!hasLogisticsData && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm space-y-2">
                  <p className="font-medium flex items-center gap-2">
                    <Truck className="h-4 w-4 shrink-0" />
                    Set up suppliers or purchase orders for reorder timing
                  </p>
                  <p className="text-muted-foreground">
                    Until you add suppliers and record purchase orders or deliveries, we show sales pace and days of stock left only — not when to order.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/suppliers">Add suppliers</Link>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/transport-logistics">Transport &amp; Logistics</Link>
                    </Button>
                  </div>
                </div>
              )}
              {topReorder.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {reorderRows.length === 0
                    ? "Nothing to track right now — sales have been too quiet to estimate."
                    : "No urgent reorders based on your delivery data — see stock pace below."}
                </p>
              ) : null}
              {topReorder.map((r) => (
                <div key={r.item_id} className="rounded-lg border border-border bg-card/50 p-4 text-sm">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="font-mono font-medium">{r.sku}</span>
                    <Badge variant="secondary">{r.name}</Badge>
                    {r.lead_source === "historical_median" && (
                      <Badge variant="outline">From deliveries</Badge>
                    )}
                    {r.lead_source === "supplier_default" && (
                      <Badge variant="outline">Supplier days</Badge>
                    )}
                  </div>
                  <p className="text-foreground leading-relaxed">{r.message}</p>
                </div>
              ))}
              {velocityOnlyRows.length > 0 && (
                <div className="space-y-3 pt-2 border-t border-border">
                  <p className="text-sm font-medium text-muted-foreground">
                    Stock pace (delivery time not set yet) — {velocityOnlyRows.length} product
                    {velocityOnlyRows.length === 1 ? "" : "s"}
                  </p>
                  {velocityOnlyRows.slice(0, 6).map((r) => (
                    <div key={r.item_id} className="rounded-lg border border-dashed border-border/80 bg-muted/20 p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-mono font-medium">{r.sku}</span>
                        <Badge variant="secondary">{r.name}</Badge>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{r.message}</p>
                    </div>
                  ))}
                  {velocityOnlyRows.length > 6 && (
                    <p className="text-xs text-muted-foreground">
                      + {velocityOnlyRows.length - 6} more in full list below
                    </p>
                  )}
                </div>
              )}
              {reorderRows.length > topReorder.length && (
                <details className="text-sm text-muted-foreground">
                  <summary className="cursor-pointer">All tracked SKUs ({reorderRows.length})</summary>
                  <ul className="mt-2 space-y-2 list-disc pl-5">
                    {reorderRows.map((r) => (
                      <li key={r.item_id}>{r.message}</li>
                    ))}
                  </ul>
                </details>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Dead stock liquidation (no sales {DEAD_STOCK_STALE_DAYS}+ days)
              </CardTitle>
              <CardDescription>
                Scenario only: recovering ~{100 - LIQUIDATION_DISCOUNT_PCT}% of tied capital if you move stock at ~{LIQUIDATION_DISCOUNT_PCT}% off — not a sales guarantee.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {deadRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No qualifying slow movers with stock and cost on hand.</p>
              ) : (
                deadRows.slice(0, 12).map((d) => (
                  <div key={d.item_id} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm">
                    <p className="font-medium">
                      You have {formatK(d.cash_tied)} sitting in <span className="font-mono">{d.sku}</span> with no sales in {d.days_without_sale}+ days.
                    </p>
                    <p className="mt-2 text-muted-foreground">
                      Consider discounting by {d.suggested_discount_pct}% — scenario recovery about {formatK(d.recovery_scenario_kwacha)} before it becomes a total loss.
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
